<template>
  <div
    class="form-action-card"
    :class="cardStateClass"
  >
    <div class="card-header">
      <div class="action-icon-badge" :style="iconBadgeStyle">
        <i :class="actionStatus.action.icon" />
      </div>
      <div class="action-info">
        <span class="action-name">
          {{ actionStatus.action.name }}
          <span v-if="schemaId" class="schema-id-badge">#{{ schemaId }}</span>
        </span>
        <span v-if="schemaDescription" class="action-description">{{ schemaDescription }}</span>
      </div>
      <div class="action-toggle">
        <label class="toggle-switch">
          <input
            type="checkbox"
            :checked="actionStatus.isEnabled"
            :disabled="isConsentAction || !actionStatus.isAvailable"
            @change="$emit('toggle-action', actionStatus.action.id, $event.target.checked)"
          >
          <span class="toggle-slider" />
        </label>
      </div>
    </div>

    <div v-if="hasContent" class="card-body">
      <!-- Consent action: restructured view -->
      <template v-if="isConsentAction && actionStatus.detectedConsents">
        <!-- Marketing section -->
        <div class="consent-section-header">
          <i class="fas fa-bullhorn" />
          {{ t('templateBuilder.fields.formActions.consent.sectionMarketing') }}
        </div>
        <div class="consent-channels">
          <div
            v-for="channel in marketingChannels"
            :key="'mkt-' + channel.channelId"
            class="consent-row"
            :class="'consent-' + channel.state"
          >
            <i :class="channelStateIcon(channel.state)" class="consent-status-icon" />
            <span class="consent-label">{{ channel.label }}</span>
            <span v-if="channel.state === 'inherited'" class="consent-inherited-badge">
              {{ t('templateBuilder.fields.formActions.consent.alreadyCollected') }}
            </span>
            <i
              v-else-if="channel.tooltip"
              v-tooltip.top="channel.tooltip"
              class="fas fa-question-circle consent-help-icon"
            />
          </div>
        </div>

        <!-- Transactional section -->
        <div class="consent-section-header consent-section-header--transactional">
          <i class="fas fa-exchange-alt" />
          {{ t('templateBuilder.fields.formActions.consent.sectionTransactional') }}
        </div>
        <div class="consent-channels">
          <div
            v-for="channel in transactionalChannels"
            :key="'txn-' + channel.id"
            class="consent-row"
            :class="channel.inherited ? 'consent-inherited' : (channel.active ? 'consent-ok' : 'consent-inactive')"
          >
            <i :class="channel.inherited ? 'fas fa-link' : (channel.active ? 'fas fa-check-circle' : 'fas fa-minus-circle')" class="consent-status-icon" />
            <span class="consent-label">{{ channel.label }}</span>
            <span v-if="channel.inherited" class="consent-inherited-badge">
              {{ t('templateBuilder.fields.formActions.consent.alreadyCollected') }}
            </span>
          </div>
        </div>

        <!-- Disclosure advisory note -->
        <div
          v-if="hasDisclosureAdvisory"
          class="consent-disclosure-advisory"
        >
          <i class="fas fa-info-circle" />
          <span>{{ t('templateBuilder.fields.formActions.consent.disclosureAdvisory') }}</span>
        </div>

        <!-- Suivi des ouvertures : consentement implicite au clic (aucune case visiteur).
             Proposé seulement si un email est collecté et qu'aucune case de consentement n'est présente. -->
        <template v-if="trackingConsent && trackingConsent.applicable">
          <div class="consent-section-header consent-section-header--tracking">
            <i class="fas fa-envelope-open-text" />
            {{ t('templateBuilder.fields.formActions.consent.sectionTracking') }}
          </div>
          <label class="tracking-consent-toggle">
            <input
              type="checkbox"
              :checked="trackingConsent.enabled"
              @change="$emit('toggle-tracking-consent', $event.target.checked)"
            >
            <span class="tracking-consent-label">
              {{ t('templateBuilder.fields.formActions.consent.trackingLabel') }}
            </span>
            <i
              v-tooltip.top="t('templateBuilder.fields.formActions.consent.trackingHelp')"
              class="fas fa-question-circle consent-help-icon"
            />
          </label>
          <div
            v-if="trackingConsent.enabled && !trackingConsent.disclosureCovered"
            class="tracking-disclosure-required"
          >
            <div class="tdr-message">
              <i class="fas fa-triangle-exclamation" />
              <span>{{ t('templateBuilder.fields.formActions.consent.trackingDisclosureRequired') }}</span>
            </div>
            <button type="button" class="tdr-cta" @click="$emit('add-disclosure-widget')">
              <i class="fas fa-plus" />
              {{ t('templateBuilder.fields.formActions.consent.addDisclosureWidget') }}
            </button>
          </div>
        </template>
      </template>

      <!-- Custom schema action: properties list -->
      <template v-else-if="isCustomSchemaAction && actionStatus.customPropertiesBySchema">
        <div class="fields-list">
          <span
            v-for="propData in flatCustomProperties"
            :key="propData.property"
            v-tooltip.right="propData.isInherited
              ? t('templateBuilder.fields.formActions.inheritedPropertyTooltip', { templateName: propData.sourceTemplate })
              : (propData.label && propData.label !== propData.property ? propData.property : null)"
            :class="['field-tag', propData.isInherited ? 'field-inherited' : 'field-available']"
          >
            <i :class="propData.isInherited ? 'fas fa-link' : 'fas fa-check'" />
            {{ propData.label || propData.property }}
          </span>
        </div>
        <div class="help-message">
          <i class="fas fa-info-circle" />
          <span v-html="t('templateBuilder.fields.formActions.customDataStorageInfo', { schemaName: `<b>${actionStatus.action.name}</b>` })" />
        </div>
      </template>

      <!-- Standard action: field tags -->
      <div v-else class="fields-list">
        <span
          v-for="field in actionStatus.availableFields"
          :key="'avail-' + field"
          v-tooltip.right="
            actionStatus.inheritedNativeProperties?.has(field)
              ? t('templateBuilder.fields.formActions.inheritedPropertyTooltip', {
                  templateName: actionStatus.inheritedNativeProperties.get(field)
                })
              : null
          "
          class="field-tag field-available"
        >
          <i class="fas fa-check" />
          {{ getPropertyDisplayName(field) }}
        </span>
        <span
          v-for="field in actionStatus.optionalAvailableFields"
          :key="'opt-' + field"
          class="field-tag field-optional"
        >
          {{ getPropertyDisplayName(field) }}
        </span>
        <span
          v-for="field in actionStatus.missingFields"
          :key="'miss-' + field"
          class="field-tag field-missing"
        >
          <i class="fas fa-times" />
          {{ getPropertyDisplayName(field) }}
        </span>
      </div>

      <!-- Help message when unavailable -->
      <div v-if="!actionStatus.isAvailable && actionStatus.missingFields.length > 0" class="help-message">
        <i class="fas fa-info-circle" />
        {{ t('templateBuilder.fields.formActions.addMissingFields') }}
      </div>

      <!-- Shopify: password collected but ignored by the platform -->
      <div v-if="showShopifyPasswordWarning" class="shopify-password-warning">
        <i class="fas fa-exclamation-circle" />
        <span>{{ t('templateBuilder.fields.formActions.shopifyPasswordWarning') }}</span>
      </div>
    </div>

    <!-- Per-action submission policy ("accept re-submission"). The whole row is a
         <label> so clicking the text also toggles the checkbox (better UX). -->
    <div
      v-if="submissionOption && actionStatus.isEnabled"
      class="consent-section-header consent-section-header--submission"
    >
      <i class="fas fa-redo" />
      {{ t('templateBuilder.fields.formActions.consent.sectionResubmission') }}
    </div>
    <label v-if="submissionOption && actionStatus.isEnabled" class="card-submission-option">
      <input
        type="checkbox"
        :checked="submissionOption.value"
        @change="$emit('toggle-submission-option', actionStatus.action.id, $event.target.checked)"
      >
      <span class="submission-option-label">{{ submissionOptionLabel }}</span>
    </label>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import type { FormActionStatus, DetectedConsent, CustomSchemaAction } from '../../composables/template-editor/FormActionsConfig';
import { getPropertyDisplayName } from '../../composables/template-editor/FormActionsEvaluator';
import { isPlatformWithoutPassword, hasPasswordFieldOnTemplate } from '../../composables/template-editor/useShopifyPasswordNotice';

interface MarketingChannel {
  channelId: string;
  label: string;
  state: 'ok' | 'warning' | 'inactive' | 'inherited';
  tooltip: string | null;
}

interface TransactionalChannel {
  id: string;
  label: string;
  active: boolean;
  inherited: boolean;
}

export default defineComponent({
  name: 'FormActionCard',
  directives: {
    tooltip: Tooltip,
  },

  props: {
    actionStatus: {
      type: Object as () => FormActionStatus,
      required: true,
    },
    submissionOption: {
      type: Object as () => { kind: 'allowResubmit' | 'allowResubmitData'; value: boolean } | null,
      default: null,
    },
  },

  emits: ['toggle-action', 'toggle-submission-option', 'toggle-tracking-consent', 'add-disclosure-widget'],

  setup(props) {
    const { t } = useI18n();

    const isConsentAction = computed(() => props.actionStatus.action.id === 'consent');
    const isCustomSchemaAction = computed(() => props.actionStatus.action.id.startsWith('custom_data_storage_'));
    const trackingConsent = computed(() => props.actionStatus.trackingConsent);

    const submissionOptionLabel = computed(() => {
      if (!props.submissionOption) return '';
      return props.submissionOption.kind === 'allowResubmitData'
        ? t('templateBuilder.formFields.allowResubmitDataLabel')
        : t('templateBuilder.formFields.allowResubmitLabel');
    });

    // Surfaces a warning on the "Création de compte" action card when the
    // platform handles authentication itself (e.g. Shopify) but the form still
    // collects a password — that password will be silently ignored at submit.
    const showShopifyPasswordWarning = computed(() => (
      props.actionStatus.action.id === 'account_creation'
      && props.actionStatus.isEnabled
      && isPlatformWithoutPassword()
      && hasPasswordFieldOnTemplate()
    ));

    const schemaId = computed(() => {
      if (!isCustomSchemaAction.value) return null;
      return (props.actionStatus.action as CustomSchemaAction).schemaId || null;
    });

    const schemaDescription = computed(() => {
      if (!isCustomSchemaAction.value) return null;
      return (props.actionStatus.action as CustomSchemaAction).schemaDescription || null;
    });

    const cardStateClass = computed(() => {
      if (props.actionStatus.isEnabled) return 'state-enabled';
      if (props.actionStatus.isAvailable) return 'state-available';
      return 'state-unavailable';
    });

    const iconBadgeStyle = computed(() => {
      const color = props.actionStatus.action.color || '#6c757d';
      if (props.actionStatus.isEnabled) {
        return { backgroundColor: `${color}14`, color };
      }
      return {};
    });

    const hasContent = computed(() => {
      if (isConsentAction.value && props.actionStatus.detectedConsents?.length) return true;
      if (isCustomSchemaAction.value && props.actionStatus.customPropertiesBySchema) return true;
      if (props.actionStatus.availableFields.length > 0) return true;
      if (props.actionStatus.optionalAvailableFields.length > 0) return true;
      if (props.actionStatus.missingFields.length > 0) return true;
      return false;
    });

    const flatCustomProperties = computed(() => {
      if (!props.actionStatus.customPropertiesBySchema) return [];
      return Object.values(props.actionStatus.customPropertiesBySchema).flat();
    });

    // --- Consent: Marketing channels ---
    const getMarketingState = (consent: DetectedConsent): 'ok' | 'warning' | 'inactive' => {
      if (!consent.identifierPresent) return 'inactive';
      if (consent.mode === 'not_configured') return 'warning';
      if (consent.mode === 'implicit' && consent.checkboxRequiredByAccountCreation) return 'warning';
      return 'ok';
    };

    const getMarketingTooltip = (consent: DetectedConsent): string | null => {
      if (!consent.identifierPresent) return null;

      if (consent.mode === 'not_configured' && consent.channelId === 'whatsapp') {
        return t('templateBuilder.fields.formActions.consent.tooltipWhatsappCheckboxRequired');
      }

      if (consent.checkboxRequiredByAccountCreation) {
        return t('templateBuilder.fields.formActions.consent.tooltipCheckboxRequiredByAccountCreation', { channel: consent.label });
      }

      return null;
    };

    const marketingChannels = computed((): MarketingChannel[] => {
      if (!props.actionStatus.detectedConsents) return [];
      return props.actionStatus.detectedConsents.map((consent) => {
        if (consent.mode === 'already_collected') {
          return {
            channelId: consent.channelId,
            label: consent.label,
            state: 'inherited' as const,
            tooltip: null,
          };
        }
        return {
          channelId: consent.channelId,
          label: consent.label,
          state: getMarketingState(consent),
          tooltip: getMarketingTooltip(consent),
        };
      });
    });

    // --- Consent: Transactional channels ---
    const transactionalChannels = computed((): TransactionalChannel[] => {
      if (!props.actionStatus.detectedConsents) return [];
      const inheritedChannelIds = new Set(
        props.actionStatus.detectedConsents
          .filter((c) => c.mode === 'already_collected')
          .map((c) => c.channelId),
      );
      const hasEmail = props.actionStatus.detectedConsents.some((c) => c.channelId === 'email' && c.identifierPresent);
      const hasPhone = props.actionStatus.detectedConsents.some((c) => (c.channelId === 'sms' || c.channelId === 'whatsapp') && c.identifierPresent);
      return [
        { id: 'email', label: t('templateBuilder.fields.formActions.consent.transactional.email'), active: hasEmail, inherited: inheritedChannelIds.has('email') },
        { id: 'sms', label: t('templateBuilder.fields.formActions.consent.transactional.sms'), active: hasPhone, inherited: inheritedChannelIds.has('sms') },
        { id: 'whatsapp', label: t('templateBuilder.fields.formActions.consent.transactional.whatsapp'), active: hasPhone, inherited: inheritedChannelIds.has('whatsapp') },
      ];
    });

    const hasDisclosureAdvisory = computed(() => {
      if (!props.actionStatus.detectedConsents) return false;
      // Only show advisory if at least one channel has marketing actively OK (not just warning)
      const hasActiveMarketingOk = props.actionStatus.detectedConsents.some(
        (c) => c.mode !== 'already_collected' && getMarketingState(c) === 'ok',
      );
      if (!hasActiveMarketingOk) return false;
      return props.actionStatus.detectedConsents.some(
        (c) => c.disclosureAdvisory && c.mode !== 'already_collected',
      );
    });

    const channelStateIcon = (state: string): string => {
      switch (state) {
        case 'ok': return 'fas fa-check-circle';
        case 'warning': return 'fas fa-exclamation-circle';
        case 'inactive': return 'fas fa-minus-circle';
        case 'inherited': return 'fas fa-link';
        default: return 'fas fa-circle';
      }
    };

    return {
      t,
      getPropertyDisplayName,
      isConsentAction,
      isCustomSchemaAction,
      trackingConsent,
      submissionOptionLabel,
      schemaId,
      schemaDescription,
      cardStateClass,
      iconBadgeStyle,
      hasContent,
      flatCustomProperties,
      marketingChannels,
      transactionalChannels,
      hasDisclosureAdvisory,
      channelStateIcon,
      showShopifyPasswordWarning,
    };
  },
});
</script>

<style scoped lang="scss">
$color-enabled: #94c840;
$color-enabled-border: #94c840;
$color-available-border: #d0d5dd;
$color-unavailable-bg: #fafbfc;
$color-text: #344054;
$color-text-secondary: #667085;
$color-text-muted: #98a2b3;
$color-border: #eaecf0;
$color-danger: #f04438;
$color-warning: #f79009;

.form-action-card {
  background: #fff;
  border: 1px solid $color-border;
  border-radius: 6px;
  border-left: 4px solid transparent;
  transition: border-color 0.2s, background-color 0.2s;
  margin-bottom: 0.5rem !important;

  &.state-enabled {
    border-left-color: $color-enabled-border;
  }

  &.state-available {
    border-left-color: $color-available-border;
  }

  &.state-unavailable {
    border-left-color: transparent;
    background-color: $color-unavailable-bg;

    .card-header, .card-body {
      opacity: 0.65;
    }
  }
}

.card-header {
  display: flex;
  align-items: center;
  padding: 0.625rem 0.75rem;
  gap: 0.625rem;
}

.action-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  background-color: #f2f4f7;
  color: $color-text-secondary;
  flex-shrink: 0;
}

.action-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  .action-name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: $color-text;
    line-height: 1.2;
  }

  .schema-id-badge {
    display: inline-block;
    font-size: 0.625rem;
    font-weight: 500;
    color: $color-text-muted;
    background: #f2f4f7;
    padding: 0 0.3rem;
    border-radius: 3px;
    margin-left: 0.375rem;
    vertical-align: middle;
  }

  .action-description {
    font-size: 0.6875rem;
    color: $color-text-secondary;
    line-height: 1.3;
    margin-top: 0.125rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.action-toggle {
  flex-shrink: 0;

  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 34px;
    height: 20px;
    cursor: pointer;

    input {
      opacity: 0;
      width: 0;
      height: 0;

      &:checked + .toggle-slider {
        background-color: $color-enabled;
      }

      &:checked + .toggle-slider:before {
        transform: translateX(14px);
      }

      &:disabled + .toggle-slider {
        cursor: not-allowed;
      }

      &:checked:disabled + .toggle-slider {
        background-color: $color-enabled;
        opacity: 0.7;
      }

      &:not(:checked):disabled + .toggle-slider {
        background-color: #e4e7ec;
      }
    }

    .toggle-slider {
      position: absolute;
      cursor: pointer;
      inset: 0;
      background-color: #d0d5dd;
      transition: 0.2s;
      border-radius: 10px;

      &:before {
        position: absolute;
        content: "";
        height: 16px;
        width: 16px;
        left: 2px;
        bottom: 2px;
        background-color: white;
        transition: 0.2s;
        border-radius: 50%;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.card-body {
  padding: 0 0.75rem 0.625rem 0.75rem;
}

.card-submission-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  user-select: none;

  input[type='checkbox'] {
    cursor: pointer;
    flex-shrink: 0;
  }

  .submission-option-label {
    font-size: 0.6875rem;
    color: $color-text-secondary;
    line-height: 1.3;
  }
}

// Consent section headers
.consent-section-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: $color-text-secondary;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.375rem 0 0.125rem 0;

  i {
    font-size: 0.625rem;
    color: $color-text-muted;
  }

  &--transactional {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid $color-border;
  }
}

// Consent channels
.consent-channels {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.consent-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.75rem;

  .consent-status-icon {
    font-size: 0.6875rem;
    flex-shrink: 0;
  }

  .consent-label {
    font-weight: 500;
    color: $color-text;
    flex: 1;
  }

  .consent-help-icon {
    font-size: 0.625rem;
    color: $color-text-muted;
    cursor: help;
    opacity: 0.6;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }
  }

  &.consent-ok {
    .consent-status-icon { color: $color-enabled; }
  }

  &.consent-warning {
    .consent-status-icon { color: $color-warning; }
  }

  &.consent-inactive {
    .consent-status-icon { color: $color-text-muted; }
    .consent-label { color: $color-text-muted; }
  }

  &.consent-inherited {
    .consent-status-icon { color: $color-text-muted; }
    .consent-label { color: $color-text-muted; }
  }

  .consent-inherited-badge {
    font-size: 0.625rem;
    color: $color-text-muted;
    font-style: italic;
    margin-left: auto;
  }
}

// Disclosure advisory
.consent-disclosure-advisory {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin-top: 0.5rem;
  padding: 0.375rem 0.5rem;
  font-size: 0.6875rem;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 4px;
  line-height: 1.4;

  i {
    font-size: 0.625rem;
    color: #f59e0b;
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
}

// Suivi des ouvertures — consentement implicite (section dédiée dans la carte Consentements)
.consent-section-header--tracking {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid $color-border;
}

.consent-section-header--submission {
  padding: 0.5rem 0.75rem 0.125rem;
  margin-top: 0;
  border-top: 1px solid $color-border;
}

.tracking-consent-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.75rem;
  cursor: pointer;
  user-select: none;

  input[type='checkbox'] {
    cursor: pointer;
    flex-shrink: 0;
  }

  .tracking-consent-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: $color-text;
    flex: 1;
  }
}

.tracking-disclosure-required {
  margin-top: 0.375rem;
  padding: 0.5rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .tdr-message {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    font-size: 0.6875rem;
    color: #b45309;
    line-height: 1.4;

    i {
      font-size: 0.625rem;
      color: #f59e0b;
      margin-top: 0.125rem;
      flex-shrink: 0;
    }
  }

  .tdr-cta {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #92400e;
    background: #fff;
    border: 1px solid #fcd34d;
    border-radius: 4px;
    padding: 0.25rem 0.625rem;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: #fffbeb; }

    i { font-size: 0.5625rem; }
  }
}

// Fields
.fields-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.field-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  font-size: 0.6875rem;
  border-radius: 3px;
  font-weight: 500;

  i { font-size: 0.5625rem; }

  &.field-available {
    background-color: rgba($color-enabled, 0.1);
    color: darken($color-enabled, 10%);

    i { color: $color-enabled; }
  }

  &.field-optional {
    background-color: #f2f4f7;
    color: $color-text-secondary;
  }

  &.field-inherited {
    background-color: #f2f4f7;
    color: $color-text-secondary;
    cursor: help;

    i { color: $color-text-muted; }
  }

  &.field-missing {
    background-color: rgba($color-danger, 0.08);
    color: $color-danger;

    i { color: $color-danger; }
  }
}

// Shopify password warning — same visual language as consent disclosure
.shopify-password-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin-top: 0.5rem;
  padding: 0.375rem 0.5rem;
  font-size: 0.6875rem;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 4px;
  line-height: 1.4;

  i {
    font-size: 0.625rem;
    color: #f59e0b;
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
}

// Help message
.help-message {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  padding: 0.375rem 0.5rem;
  font-size: 0.6875rem;
  color: $color-text-muted;
  background: #f9fafb;
  border-radius: 4px;

  i {
    font-size: 0.625rem;
    color: #d0d5dd;
  }
}
</style>
